import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridRowSelectionModel,
  GridRowsProp,
} from "@mui/x-data-grid";
import React, { useCallback, useMemo, useState } from "react";

// Helper function to get displayable text from any value
const getDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) return "N/A";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value !== "object") return "N/A";

  const obj = value as Record<string, unknown>;
  // Find first property with string value
  const displayKey = Object.keys(obj).find((key) => {
    const val = obj[key];
    return typeof val === "string" && val;
  });

  return displayKey && typeof obj[displayKey] === "string" ? (obj[displayKey] as unknown as string) : "N/A";
};

interface ReusableDataGridProps<T> {
  columns: GridColDef[];
  data: T[];
  height?: number;
  minWidth?: number;
  onRowClick?: GridEventListener<"rowClick">;
  onSelectionChange?: (
    selection: GridRowSelectionModel,
    selectedRows: T[]
  ) => void;
  sx?: SxProps<Theme>;
  getRowId?: (row: T) => string | number;
  hideCheckboxSelection?: boolean;
  hideFilters?: boolean;
}

export function ReusableDataGrid<T>({
  columns,
  data,
  height = 600,
  minWidth = 800,
  onRowClick,
  onSelectionChange,
  getRowId,
  hideCheckboxSelection = false,
  hideFilters = false,
  sx = {},
}: ReusableDataGridProps<T>) {
  const [filters, setFilters] = useState<Record<string, Set<unknown>>>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeFilterField, setActiveFilterField] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  // Handle filter button click
  const handleFilterClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, field: string) => {
      setAnchorEl(event.currentTarget);
      setActiveFilterField(field);
      setFilterSearch("");
    },
    []
  );

  // Handle closing the filter popover
  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
    setActiveFilterField(null);
    setFilterSearch("");
  }, []);

  // Handle filter value change
  const handleFilterChange = useCallback((value: unknown, field: string) => {
    setFilters((prev) => {
      const updated = new Set(prev[field] || []);
      updated.has(value) ? updated.delete(value) : updated.add(value);
      return { ...prev, [field]: updated };
    });
  }, []);

  // Clear specific filter
  const handleClearFilter = useCallback((field: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    handleCloseFilter();
  }, [handleCloseFilter]);

  // Filter data based on active filters
  const filteredData = useMemo(() => {
    return data.filter((row) =>
      Object.entries(filters).every(([field, values]) =>
        values.size === 0 ? true : values.has((row as Record<string, unknown>)[field])
      )
    );
  }, [data, filters]);

  // Prepare rows for DataGrid
  const rows: GridRowsProp = useMemo(() => {
    return filteredData.map((item, index) => ({
      id: getRowId ? getRowId(item) : (item as Record<string, unknown>).id ?? index,
      ...item,
    }));
  }, [filteredData, getRowId]);

  // Handle row selection change
  const handleSelectionChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      if (!Array.isArray(selectionModel)) return;
      const selectedRows = selectionModel
        .map((id) => rows.find((row) => row.id === id))
        .filter(Boolean) as T[];
      onSelectionChange?.(selectionModel, selectedRows);
    },
    [rows, onSelectionChange]
  );

  // Memoized filtered columns with filter icons
  const filteredColumns = useMemo(() => {
    if (hideFilters) {
      return columns.map((col) => ({
        ...col,
        sortable: false,
        filterable: false,
      }));
    }
    
    return columns.map((col) => ({
      ...col,
      sortable: false,
      filterable: false,
      renderHeader: () => {
        const hasFilter = filters[col.field]?.size > 0;

        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography fontWeight={600}>{col.headerName}</Typography>
            <Badge
              variant="dot"
              color="primary"
              invisible={!hasFilter}
              overlap="circular"
            >
              <IconButton
                size="small"
                onClick={(e) => handleFilterClick(e, col.field)}
                aria-label={`Filter ${col.headerName}`}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Badge>
          </Box>
        );
      },
    }));
  }, [columns, filters, handleFilterClick, hideFilters]);

  // Get unique filter values based on search
  const uniqueValues = useMemo(() => {
    if (!activeFilterField) return [];
    const valueSet = new Set(
      data.map((row) => (row as Record<string, unknown>)[activeFilterField])
    );
    const allOptions = Array.from(valueSet);
    if (allOptions.length <= 5) return allOptions;
    return allOptions.filter((val) =>
      String(val).toLowerCase().includes(filterSearch.toLowerCase())
    );
  }, [activeFilterField, data, filterSearch]);

    // Show loading for initial render, then turn off
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Reset loading when data changes
  React.useEffect(() => {
    if (data.length > 0) {
      setIsLoading(false);
    }
  }, [data]);

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <div style={{ height, minWidth }}>
          <DataGrid
            rows={rows}
            columns={filteredColumns}
            loading={isLoading && data.length === 0}
            checkboxSelection={!hideCheckboxSelection}
            onRowClick={onRowClick}
            onRowSelectionModelChange={handleSelectionChange}
            disableRowSelectionOnClick
            autoHeight
            hideFooterSelectedRowCount
            sx={{
              fontFamily: "Quicksand, sans-serif",
              border: "none",
              minHeight: "60vh",
              "& .MuiDataGrid-cell": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#f5f5f5",
              },
              ...sx,
            }}
          />
        </div>
      </div>

      {/* Filter Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, maxHeight: 320, overflowY: "auto", width: 250 }}>
          <Typography fontWeight={600} mb={1}>
            Filter: {activeFilterField}
          </Typography>

          {/* Search box for filter options when count > 5 */}
          {data.length > 0 &&
            new Set(
              data.map((row) => (row as Record<string, unknown>)[activeFilterField!])
            ).size > 5 && (
              <TextField
                size="small"
                placeholder="Search options..."
                fullWidth
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                sx={{ mb: 1 }}
              />
            )}

          {/* Filter options list */}
          {uniqueValues.map((val, idx) => (
            <FormControlLabel
              key={`${activeFilterField}-${idx}`}
              control={
                <Checkbox
                  size="small"
                  checked={filters[activeFilterField!]?.has(val) || false}
                  onChange={() => handleFilterChange(val, activeFilterField!)}
                />
              }
              label={getDisplayValue(val)}
            />
          ))}

          {/* Clear filter button */}
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              size="small"
              onClick={() => handleClearFilter(activeFilterField!)}
              color="secondary"
              variant="outlined"
            >
              Clear Filter
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
